import React, { Dispatch } from 'react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/shadcn/skeleton';
import { Button } from '@/components/shadcn/button';
import { ScoutErrorType } from '@/types/location';
import { useMapCredits } from '@/hooks/useMapCredits';
import { validateOrganizationFunded } from '@/api/organization';
import * as Toast from '@/components/ui/toast/Toast';
import { BURST_PRICE } from '@/components/ui/scout/Scout';
import { useUser } from '@/hooks/useUser';

interface Props {
  length?: number;
  apiCallsComplete: boolean;
  errorReason?: ScoutErrorType;
  id: string;
  setBurstModalOptions: Dispatch<{
    isOpen: boolean;
    locationsIds: string[];
    successCallback: () => void;
  }>;
  hasActiveBurst: boolean;
}

export default function ImagerySkeleton({
  length = 4,
  apiCallsComplete,
  errorReason,
  id,
  setBurstModalOptions,
  hasActiveBurst,
}: Props) {
  const { user } = useUser();
  const { setCreditsModalOptions } = useMapCredits();

  const handleAddBurst = async () => {
    const response = await validateOrganizationFunded(
      user?.currentOrganization,
    );

    if ('error' in response) {
      console.error(response.error);
      toast.custom(t => (
        <Toast.Default
          title="Try Again"
          description="Something went wrong, please try again."
          buttons={[
            {
              label: 'Dismiss',
              onClick: () => toast.dismiss(t),
              variant: 'secondary',
              size: 'fw',
            },
          ]}
        />
      ));
      return;
    }

    if (response.credits >= BURST_PRICE) {
      setBurstModalOptions({
        isOpen: true,
        locationsIds: [id],
        successCallback: () => {},
      });
    } else {
      toast.custom(t => (
        <Toast.Default
          title="Map Credits Needed"
          description="Purchase map credits to continue using Scout."
          buttons={[
            {
              label: 'Dismiss',
              onClick: () => toast.dismiss(t),
              variant: 'secondary',
              size: 'fw',
            },
            {
              label: 'Purchase Credits',
              onClick: () => {
                setCreditsModalOptions({ isVisible: true });
                toast.dismiss(t);
              },
              variant: 'default',
              size: 'fw',
            },
          ]}
        />
      ));
    }
  };

  const errorReasons = {
    mapCredits: {
      title: 'Unable to View Imagery',
      description: 'Not Enough Map Credits',
      CTA: (
        <Button
          onClick={() => {
            setCreditsModalOptions({
              isVisible: true,
            });
          }}>
          Purchase Map Credits
        </Button>
      ),
    },
    imagery: {
      title: hasActiveBurst ? 'Burst Active' : 'No Imagery Found',
      description: hasActiveBurst
        ? 'Contributors have been notified. Check back later for updates.'
        : 'Bursts provide on-demand, targeted, fresh map data',
      CTA: !hasActiveBurst ? (
        <Button
          className="bg-harvestGoldWarning-600 hover:bg-harvestGoldWarning-700"
          onClick={handleAddBurst}>
          Burst
        </Button>
      ) : null,
    },
    unknown: {
      title: 'Unable to Load Imagery',
      description: 'Imagery failed to load. Refresh the page to try again.',
      CTA: <></>,
    },
  };

  return (
    <div
      data-complete={apiCallsComplete}
      className="group/skeleton no-scrollbar relative mt-3 flex w-full flex-row overflow-x-scroll focus:outline-none data-[complete=true]:overflow-x-hidden"
      tabIndex={0}>
      {new Array(length).fill(0).map((_, index) => {
        return (
          <div
            key={index.toString()}
            className="mr-2 min-w-[60%] transition-all duration-1000 data-[last=true]:mr-0 group-data-[complete=true]/skeleton:opacity-0 md:min-w-[30%]">
            <div className="flex flex-col gap-2">
              <div className="aspect-h-1 aspect-w-2 flex w-full">
                <Skeleton />
              </div>
              <Skeleton className="h-6" />
              <Skeleton className="h-4" />
            </div>
          </div>
        );
      })}
      {errorReason ? (
        <div className="absolute left-1/2 top-1/2 flex w-full -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2 opacity-0 transition-all duration-1000 group-data-[complete=true]/skeleton:opacity-100">
          <h4 className="text-center text-base font-bold md:text-xl">
            {errorReasons[errorReason].title}
          </h4>
          <p className="text-xs md:text-sm">
            {errorReasons[errorReason].description}
          </p>
          {errorReasons[errorReason].CTA}
        </div>
      ) : null}
    </div>
  );
}
